import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import Button from "../../../components/common/Button";
import DocumentPreview from "./DocumentPreview";
import delet from "../../../assets/tips/delete.svg";
import eye from "../../../assets/posts/eye.svg";

// Mirrors the backend contract. Kept here so the user gets instant feedback
// rather than waiting for a 400.
export const ALLOWED_EXTENSIONS = ["pdf", "ppt", "pptx", "doc", "docx"];
export const MAX_BYTES = 100 * 1024 * 1024; // 100MB

const extensionOf = (name = "") => name.split(".").pop()?.toLowerCase() || "";

/**
 * Document media editor — upload only, no generation.
 *
 * Props follow the same contract as ImageSection / CarouselSection:
 *   file        — the File the user picked (null if none yet)
 *   url         — a stored document_url, when editing a saved post
 *   filename    — stored filename, when editing a saved post
 *   onFileSelect(file)
 *   onDelete()
 */
export default function DocumentMedia({
  file = null,
  url = null,
  filename = null,
  onFileSelect,
  onDelete,
  disabled = false,
  onPreviewClick,
  onDownload,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const validate = (candidate) => {
    const ext = extensionOf(candidate.name);

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return t(
        "document.errors.format",
        "The document format is not supported. Allowed formats: PDF, PPT, PPTX, DOC, DOCX."
      );
    }
    if (candidate.size > MAX_BYTES) {
      return t(
        "document.errors.size",
        "The document exceeds the maximum allowed size of 100MB."
      );
    }
    return null;
  };

  const handleChange = (e) => {
    const candidate = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after an error
    if (!candidate) return;

    const message = validate(candidate);
    if (message) {
      setError(message);
      return;
    }

    setError(null);
    onFileSelect?.(candidate);
  };

  const handleDelete = () => {
    setError(null);
    onDelete?.();
  };

  const hasDocument = !!file || !!url;

  return (
    <div id="section-document" className="flex-1 flex flex-col">
      <input
        type="file"
        ref={inputRef}
        accept=".pdf,.ppt,.pptx,.doc,.docx"
        onChange={handleChange}
        className="hidden"
      />

      {!hasDocument ? (
        <>
          <h3 className="text-xl font-bold mb-2">
            {t("document.title")}
          </h3>
          <p className="text-xl text-black-800 mb-4">
            {t("document.description")}
          </p>

          <Button
            variant="dark"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="w-full gap-2"
          >
            {t("document.upload")}
          </Button>

          <p className="text-sm text-black-400 mt-2">
            {t("document.hint")}
          </p>
        </>
      ) : (
        <>
          {/* Toolbar — matches the image and carousel sections */}
          <DocumentPreview
            file={file}
            url={url}
            filename={filename || file?.name}
            title={t("document.preview")}
            actions={
              <>
                <ToolbarTooltipButton label={t("insights.preview")} onClick={onPreviewClick}>
                  <img src={eye} alt="preview" className="w-4 h-4" />
                </ToolbarTooltipButton>
                <ToolbarTooltipButton
                  label={t("buttons.remove")}
                  onClick={handleDelete}
                  disabled={disabled}
                >
                  <img src={delet} alt="remove" className="w-4 h-4" />
                </ToolbarTooltipButton>
              </>
            }
          />
        </>
      )}

      {error && (
        <div className="mt-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}
    </div>
  );
}